import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Card, makeStyles } from '@material-ui/core';
import { Page } from 'src/components';
import {
  QuotationV2Body,
  QuotationV2Footbar,
  QuotationV2Toolbar
} from './components';
import { QuotationHeader } from './components/QuotationHeader';
import { QuotationFooterNote } from './components/QuotationFooterNote';
import { theme } from 'src/theme';
import { QuotationSubheader } from './components/QuotationSubheader';
import useQuotationApi from 'src/hooks/quotation-v2/use-quotation-api';
import useQuotationProduct from 'src/hooks/quotation-v2/use-quotation-products';
import { initialQuotationState } from './constant';
import useUserInfo from 'src/hooks/user/use-user-info';
import html2canvas from 'html2canvas';
import { useSnackBar } from 'src/hooks';
import { QuotationPrintModal } from '../quotation/components/QuotationPrintModal';
import { flushSync } from 'react-dom';

const useStyles = makeStyles(() => ({
  root: {
    padding: '2em',
    display: 'flex',
    flexDirection: 'column',
    gap: '1em'
  },
  box: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%'
  },
  canvas: {
    padding: theme.spacing(3),
    backgroundSize: '80px 70px',
    backgroundRepeat: 'repeat',
    background:
      'linear-gradient(rgba(255,255,255,.95), rgba(255,255,255,.95)), url("/static/images/avatars/pcworth_logo.png")',
    width: '920px'
  }
}));

export const QuotationV2View: FC = () => {
  const cardRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const {
    categoryOptions,
    fetchCategoryOption,
    fetchQuotationProducts
  } = useQuotationApi();
  const {
    productOptions,
    quotationParams,
    templateDetails
  } = useQuotationProduct();
  const {
    favoriteBranch,
    userBranchIdDefaultOption,
    userDetails,
    userBranchOptions
  } = useUserInfo();

  const branchInfo = useMemo(() => {
    const filteredValue = userDetails?.branches?.find(
      (branch) => branch.id === quotationParams?.branch_id
    );
    return filteredValue;
  }, [quotationParams.branch_id, userDetails]);
  const snackBar = useSnackBar();

  const [openPrintModal, setOpenPrintModal] = useState<boolean>(false);
  const [isScreenCapture, setIsScreenCapture] = useState<boolean>(false);

  useEffect(() => {
    if (categoryOptions?.length <= 0) {
      fetchCategoryOption();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryOptions]);

  //initialize state quotation products
  useEffect(() => {
    if (productOptions) {
      const categIds: any = initialQuotationState?.map(
        (quotation) => quotation?.product?.category_id
      );

      fetchQuotationProducts({
        category_ids: categIds || [],
        branch_id: favoriteBranch || userBranchIdDefaultOption,
        page: 1
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const captureQuotation = async () => {
    if (!cardRef.current) return;

    flushSync(() => {
      setIsScreenCapture(true);
    });

    const canvas = await html2canvas(cardRef.current, {
      scale: 2,
      backgroundColor: '#fff'
    });
    canvas.toBlob(
      (blob: any) =>
        navigator.clipboard
          // eslint-disable-next-line no-undef
          ?.write([new ClipboardItem({ 'image/png': blob })])
          ?.then(() => {
            setIsScreenCapture(false);
            snackBar.show({
              severity: 'success',
              message: 'Image copied to clipboard'
            });
          })
          .catch(),
      'image/png',
      99
    );

    setIsScreenCapture(false);
  };

  return (
    <Page className={classes.root} title="Quotation V2">
      <QuotationV2Toolbar />
      <Box className={classes.box}>
        <Card className={classes.canvas} ref={cardRef}>
          <QuotationHeader branchInfo={branchInfo} />
          <QuotationSubheader
            customerName={quotationParams?.customer_name}
            branchName={
              branchInfo?.branch_name
                ? branchInfo.branch_name.charAt(0).toUpperCase() +
                  branchInfo.branch_name.slice(1).toLowerCase()
                : ''
            }
          />
          <QuotationV2Body isScreenCapture={isScreenCapture} />
          <QuotationFooterNote />
        </Card>
      </Box>

      <QuotationV2Footbar
        onCapture={captureQuotation}
        openPrintSlipModal={() => setOpenPrintModal(true)}
      />

      <QuotationPrintModal
        isOpen={openPrintModal}
        quotationTemplateItems={templateDetails || undefined}
        showSRP={quotationParams?.is_srp}
        branchInfo={branchInfo}
        userBranchOptions={userBranchOptions}
        onHandleClose={() => setOpenPrintModal(false)}
      />
    </Page>
  );
};
